import React from "react";
import Nav from "../components/nav";
import Footer from "../components/footer";
import { timeline, Hobbies, interests } from "../data/cart.js";
import { useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import AnimatedSection from "../components/AnimatedSection";

function AboutMe() {
  const [selectedTab, setSelectedTab] = useState("journey");

  usePageMeta("/about");

  return (
    <>
      <Nav />
      <main>

      <section className="bg-textColor pt-24 pb-12 md:pb-20">
        <div className="container mx-auto px-4 md:px-8">
          <AnimatedSection animation="fade-down" className="text-center">
            <div className="kicker">About</div>
            <h1 className="display-title text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
              About Me
            </h1>
            <div className="w-24 h-1 bg-secondary mx-auto rounded-full"></div>
          </AnimatedSection>
        </div>
      </section>

      <section className="flex flex-col lg:flex-row w-full min-h-[30rem] px-4 md:px-8 lg:px-40 items-center py-8 lg:py-0" aria-labelledby="my-story-heading">
        <AnimatedSection animation="fade-left" className="w-full lg:w-2/5 flex items-center justify-center mb-8 lg:mb-0">
          <img
            src="/profile1.png"
            alt="Rojesh Thapa portrait"
            width="448"
            height="448"
            className="object-cover h-64 md:h-80 lg:h-full w-full max-w-md lg:max-w-none rounded-xl"
          />
        </AnimatedSection>
        <AnimatedSection animation="fade-right" className="w-full lg:w-[60%] lg:ml-12 text-justify h-full flex flex-col justify-center">
          <h2 id="my-story-heading" className="display-title text-2xl md:text-3xl font-bold mb-4 text-center lg:text-left">
            My Story
          </h2>
          <p>
            I'm Rojesh Thapa, a committed BIT student at Itahari International
            College (IIC), pursuing a degree B.Sc. (Hons) Computing program,
            affiliated with London Metropolitan University (UK).
          </p>
          <p className="mt-4">
            I am a full-stack focused developer with a strong foundation in
            software engineering, problem solving, and system design. I work
            comfortably across both frontend and backend technologies, building
            responsive user interfaces and scalable server-side logic using
            JavaScript, React, and Node.js. My approach emphasizes clean
            architecture, efficient code, and practical solutions to real-world
            problems.
          </p>
          <p className="mt-4">
            My frontend work focuses on accessible layouts, clear interaction
            patterns, responsive Tailwind CSS interfaces, and UI decisions that
            make applications easier to understand. On the backend side, I am
            building experience with Node.js, Express.js, SQL, MongoDB, and
            PostgreSQL so I can connect polished interfaces to useful systems.
          </p>
          <p className="mt-4">
            I focus on writing clean, maintainable code and designing efficient,
            scalable systems that prioritize performance, security, and user
            experience. I continuously refine my skills, follow best development
            practices, and approach every problem with a structured and
            analytical mindset. With a strong interest in cybersecurity, I aim
            to build secure and reliable software that users and organizations
            can trust.
          </p>
          <p className="mt-4">
            You can explore my <Link to="/projects" className="text-primary underline decoration-secondary decoration-2 underline-offset-4">web application projects</Link> or review my <Link to="/skills" className="text-primary underline decoration-secondary decoration-2 underline-offset-4">technical skill set</Link> to see the areas I am actively developing.
          </p>
        </AnimatedSection>
      </section>

      <section className="bg-ambient flex flex-wrap justify-center gap-8 p-8" aria-label="Education, interests, and goals">
        {[
          {
            title: "Education",
            icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
            content: (
              <div className="space-y-2">
                <p className="font-semibold">Bsc(Hons) Computing</p>
                <p>Itahari International College</p>
              </div>
            ),
          },
          {
            title: "Interests",
            icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
            content: (
              <ul className="space-y-2">
                {["Ethical Hacking", "Web Development", "Mobile App Development", "Cybersecurity"].map((item) => (
                  <li key={item} className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-secondary rounded-full mr-3"></span>
                    {item}
                  </li>
                ))}
              </ul>
            ),
          },
          {
            title: "Goals",
            icon: "M13 10V3L4 14h7v7l9-11h-7z",
            content: (
              <ul className="text-gray-700 space-y-2">
                {["Become a Full Stack Developer", "Open Source Projects", "Learn Cloud Computing", "Research Publication"].map((item) => (
                  <li key={item} className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-secondary rounded-full mr-3"></span>
                    {item}
                  </li>
                ))}
              </ul>
            ),
          },
        ].map((card, index) => (
          <AnimatedSection key={card.title} animation="scale-up" delay={index * 150} className="flex-1 min-w-[280px] max-w-xs flex flex-col">
            <div className="card-surface flex-1 flex flex-col p-8 rounded-2xl hover:scale-105 transition-transform">
              <div className="text-center flex-1 flex flex-col">
                <div className="w-16 h-16 bg-primary bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-primary">{card.title}</h3>
                {card.content}
              </div>
            </div>
          </AnimatedSection>
        ))}
      </section>

      <section className="bg-textColor py-20" aria-labelledby="journey-heading">
        <div className="container mx-auto px-8">
          <AnimatedSection animation="fade-up" className="text-center mb-12">
            <div className="kicker">Milestones</div>
            <h2 id="journey-heading" className="display-title text-4xl md:text-5xl font-bold text-primary mb-4">Explore My Journey</h2>
            <div className="w-24 h-1 bg-secondary mx-auto rounded-full"></div>
          </AnimatedSection>

          <AnimatedSection animation="fade-up" className="flex justify-center mb-12">
            <div className="flex flex-wrap justify-center gap-2 p-2 bg-white rounded-2xl border border-gray-200 shadow-sm max-w-full">
              {["journey", "interests", "hobbies"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={`px-4 sm:px-8 py-3 rounded-xl transition-all duration-300 text-sm font-semibold capitalize ${
                    selectedTab === tab
                      ? "bg-secondary text-textColor shadow-lg scale-105"
                      : "text-gray-600 hover:text-primary hover:bg-gray-200"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </AnimatedSection>

          <div className="max-w-6xl mx-auto">
            {selectedTab === "journey" && (
              <div className="animate-fadeIn">
                <h3 className="text-center text-3xl md:text-4xl font-bold mb-8 text-secondary display-title">Academic Journey</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {timeline.map((item, index) => (
                    <AnimatedSection key={item.id} animation="fade-up" delay={index * 100}>
                      <div className="card-surface p-8 rounded-2xl hover:scale-105 transition-transform flex flex-col h-full">
                        <div className="text-center flex-1 flex flex-col">
                          <div className="w-16 h-16 bg-secondary bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl font-bold text-secondary">{item.year}</span>
                          </div>
                          <p>{item.event}</p>
                        </div>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            )}

            {selectedTab === "interests" && (
              <div className="animate-fadeIn">
                <h3 className="text-center text-3xl md:text-4xl font-bold mb-8 text-secondary display-title">My Interests</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {interests.map((interest, index) => (
                    <AnimatedSection key={interest.id} animation="fade-up" delay={index * 100}>
                      <div className="card-surface p-8 rounded-2xl hover:scale-105 transition-transform flex flex-col h-full">
                        <div className="text-center flex-1 flex flex-col">
                          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          </div>
                          <h4 className="text-xl font-bold text-purple-600 mb-3">{interest.interest}</h4>
                          <p className="text-gray-700 leading-relaxed">{interest.description}</p>
                        </div>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            )}

            {selectedTab === "hobbies" && (
              <div className="animate-fadeIn">
                <h3 className="text-center text-3xl md:text-4xl font-bold mb-8 text-secondary display-title">Personal Hobbies</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {Hobbies.map((hobby, index) => (
                    <AnimatedSection key={hobby.id} animation="scale-up" delay={index * 100}>
                      <div className="card-surface p-8 rounded-2xl hover:scale-105 transition-transform flex flex-col h-full">
                        <div className="text-center flex-1 flex flex-col">
                          <div className="text-sm uppercase tracking-wide text-gray-500 mb-4">{hobby.icon}</div>
                          <h4 className="text-lg font-bold text-pink-600">{hobby.hobby}</h4>
                        </div>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-secondary py-6">
        <div className="container mx-auto px-8 text-center">
          <AnimatedSection animation="fade-up" className="max-w-4xl mx-auto">
            <h2 className="display-title text-4xl md:text-5xl font-bold text-primary">Let's Connect!</h2>
            <div className="w-24 h-1 bg-primary mx-auto mb-8 rounded-full"></div>
            <p className="text-lg md:text-xl text-primary mb-8 leading-relaxed">
              Interested in collaborating on tech projects or just want to chat
              about development, design, or the latest in tech? I'd love to hear
              from you!
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/contact" className="bg-primary text-textColor font-bold py-4 px-8 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg">
                  <span className="flex items-center">
                    Get in Touch
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
              </Link>
              <Link to="/projects" className="bg-primary text-textColor font-bold py-4 px-8 rounded-lg transition-all duration-300 hover:scale-105">
                  <span className="flex items-center">
                    View My Work
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </span>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
      </main>

      <Footer />
    </>
  );
}

export default AboutMe;
